import { engine } from '../audio/engine';
import { useStore } from '../store/useStore';
import type { FilterKind } from '../types';
import { Select } from './ui/Controls';
import { Knob } from './ui/Knob';
import { Meter } from './ui/Meter';
import { Spectrum } from './ui/Spectrum';

const FILTERS: { value: FilterKind; label: string }[] = [
  { value: 'off', label: 'No filter' },
  { value: 'lowpass', label: 'Low pass' },
  { value: 'highpass', label: 'High pass' },
  { value: 'bandpass', label: 'Band pass' },
];

/** Channel strips plus the master bus and its two shared sends. */
export function MixView() {
  const project = useStore((s) => s.project);
  const store = useStore();

  return (
    <div className="flex h-full min-h-0 gap-2 overflow-x-auto p-4">
      {project.tracks.map((track) => (
        <div
          key={track.id}
          className="flex w-40 shrink-0 flex-col gap-2 rounded-xl border border-ink-700 bg-ink-900 p-2.5"
          style={{ borderTopColor: track.color, borderTopWidth: 2 }}
        >
          <div className="truncate text-[11px] font-semibold" style={{ color: track.color }}>
            {track.name}
          </div>

          <div className="grid grid-cols-2 gap-y-2">
            <Knob
              label="Pitch"
              value={track.pitch}
              min={-24}
              max={24}
              step={1}
              bipolar
              color={track.color}
              format={(v) => `${v > 0 ? '+' : ''}${v.toFixed(0)}st`}
              onChange={(v) => store.updateTrack(track.id, { pitch: v })}
            />
            <Knob
              label="Drive"
              value={track.drive}
              min={0}
              max={1}
              color={track.color}
              format={(v) => `${Math.round(v * 100)}%`}
              onChange={(v) => store.updateTrack(track.id, { drive: v })}
            />
          </div>

          <Select<FilterKind>
            className="w-full !text-[10px]"
            value={track.filter}
            options={FILTERS}
            onChange={(v) => store.updateTrack(track.id, { filter: v })}
          />
          {track.filter !== 'off' && (
            <div className="grid grid-cols-2">
              <Knob
                label="Cutoff"
                value={track.cutoff}
                min={40}
                max={18000}
                curve={3}
                color={track.color}
                format={(v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v.toFixed(0))}
                onChange={(v) => store.updateTrack(track.id, { cutoff: v })}
              />
              <Knob
                label="Res"
                value={track.resonance}
                min={0.1}
                max={20}
                curve={2}
                color={track.color}
                onChange={(v) => store.updateTrack(track.id, { resonance: v })}
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-y-2 border-t border-ink-700 pt-2">
            <Knob
              label="Verb"
              value={track.sends.reverb}
              min={0}
              max={1}
              color="#38bdf8"
              format={(v) => `${Math.round(v * 100)}`}
              onChange={(v) =>
                store.updateTrack(track.id, { sends: { ...track.sends, reverb: v } })
              }
            />
            <Knob
              label="Delay"
              value={track.sends.delay}
              min={0}
              max={1}
              color="#a78bfa"
              format={(v) => `${Math.round(v * 100)}`}
              onChange={(v) => store.updateTrack(track.id, { sends: { ...track.sends, delay: v } })}
            />
            <Knob
              label="Pan"
              value={track.pan}
              min={-1}
              max={1}
              bipolar
              color={track.color}
              format={(v) =>
                Math.abs(v) < 0.02 ? 'C' : `${v < 0 ? 'L' : 'R'}${Math.abs(v * 100).toFixed(0)}`
              }
              onChange={(v) => store.updateTrack(track.id, { pan: v })}
            />
            <Knob
              label="Level"
              value={track.volume}
              min={0}
              max={1.4}
              color={track.color}
              format={(v) => `${(20 * Math.log10(Math.max(0.001, v))).toFixed(1)}dB`}
              onChange={(v) => store.updateTrack(track.id, { volume: v })}
            />
          </div>

          <Meter analyser={engine.trackAnalyser(track.id)} className="h-2 w-full rounded-sm" />

          <div className="mt-auto flex gap-1">
            <button
              className={`btn flex-1 justify-center ${track.mute ? 'btn-active' : ''}`}
              onClick={() => store.updateTrack(track.id, { mute: !track.mute })}
            >
              Mute
            </button>
            <button
              className={`btn flex-1 justify-center ${track.solo ? 'btn-active' : ''}`}
              onClick={() => store.updateTrack(track.id, { solo: !track.solo })}
            >
              Solo
            </button>
          </div>
        </div>
      ))}

      <div className="flex w-44 shrink-0 flex-col gap-2 rounded-xl border border-volt-500/40 bg-ink-900 p-2.5">
        <div className="text-[11px] font-semibold text-volt-400">Master</div>
        <div className="grid grid-cols-2 gap-y-2">
          <Knob
            label="Volume"
            value={project.master.volume}
            min={0}
            max={1.4}
            format={(v) => `${(20 * Math.log10(Math.max(0.001, v))).toFixed(1)}dB`}
            onChange={(v) => store.set((p) => void (p.master.volume = v))}
          />
          <Knob
            label="Ceiling"
            value={project.master.ceiling}
            min={-24}
            max={0}
            format={(v) => `${v.toFixed(1)}dB`}
            onChange={(v) => store.set((p) => void (p.master.ceiling = v))}
          />
          <Knob
            label="Verb size"
            value={project.master.reverbSize}
            min={0.2}
            max={6}
            color="#38bdf8"
            format={(v) => `${v.toFixed(1)}s`}
            onChange={(v) => store.set((p) => void (p.master.reverbSize = v))}
          />
          <Knob
            label="Verb tail"
            value={project.master.reverbDecay}
            min={0.5}
            max={8}
            color="#38bdf8"
            format={(v) => v.toFixed(1)}
            onChange={(v) => store.set((p) => void (p.master.reverbDecay = v))}
          />
          <Knob
            label="Delay"
            value={project.master.delayTime}
            min={0.0625}
            max={4}
            curve={2}
            color="#a78bfa"
            format={(v) => `${v.toFixed(2)}b`}
            onChange={(v) => store.set((p) => void (p.master.delayTime = v))}
          />
          <Knob
            label="Feedback"
            value={project.master.delayFeedback}
            min={0}
            max={0.92}
            color="#a78bfa"
            format={(v) => `${Math.round(v * 100)}%`}
            onChange={(v) => store.set((p) => void (p.master.delayFeedback = v))}
          />
        </div>
        <Spectrum
          analyser={engine.masterAnalyser()}
          bars={26}
          className="h-20 w-full rounded border border-ink-700"
        />
        <Meter analyser={engine.masterAnalyser()} className="h-2 w-full rounded-sm" />
        <button
          className={`btn justify-center ${project.master.metronome ? 'btn-active' : ''}`}
          onClick={() => store.set((p) => void (p.master.metronome = !p.master.metronome))}
        >
          Metronome
        </button>
      </div>
    </div>
  );
}
